// layouts/RevenueBilling/index.js
import React, { useState, useEffect } from 'react';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import {
  Card,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Collapse,
  IconButton,
  Icon,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Pagination,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { grey } from '@mui/material/colors';
import { renderFields } from '../utils/fieldRenderer';

function RevenueBilling({ guidedSellingProcesses, loading, onItemSelect, selectedItems }) {
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [expandedProcess, setExpandedProcess] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [expandedCondition, setExpandedCondition] = useState(null);
  
  // Search and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 8;

  useEffect(() => {
    setSelectedProcesses(selectedItems?.guidedSellingProcesses || []);
  }, [selectedItems]);

  // Safety checks
  if (loading) {
    return (
      <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={24} />
        <ArgonTypography variant="body2" sx={{ ml: 2 }}>
          Loading guided selling processes...
        </ArgonTypography>
      </ArgonBox>
    );
  }

  if (!guidedSellingProcesses || !Array.isArray(guidedSellingProcesses) || guidedSellingProcesses.length === 0) {
    return (
      <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <ArgonTypography variant="body2" color="textSecondary">No guided selling processes found.</ArgonTypography>
      </ArgonBox>
    );
  }

  const handleProcessSelect = (process, isSelected) => {
    let newSelection;
    if (isSelected) {
      newSelection = [...selectedProcesses, process];
    } else {
      newSelection = selectedProcesses.filter(
        (p) => p.guidedSellingProcess?.sfId !== process.guidedSellingProcess?.sfId
      );
    }
    setSelectedProcesses(newSelection);
    onItemSelect('guidedSellingProcesses', newSelection);
  };

  const isProcessSelected = (process) => {
    return selectedProcesses.some((p) => p.guidedSellingProcess?.sfId === process.guidedSellingProcess?.sfId);
  };
  if (loading) {
    return (
      <Card>
        <ArgonBox p={3} textAlign="center">
          <CircularProgress />
        </ArgonBox>
      </Card>
    );
  }

  // Filter and paginate
  const filteredProcesses = guidedSellingProcesses.filter((processGroup) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return processGroup.guidedSellingProcess?.name?.toLowerCase().includes(searchLower);
  });

  const pageCount = Math.ceil(filteredProcesses.length / rowsPerPage);
  const paginatedProcesses = filteredProcesses.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <Card>
      <ArgonBox p={3}>
        {/* Search Bar */}
        <ArgonBox mb={2} display="flex" justifyContent="flex-end">
          <TextField
            size="medium"
            placeholder="Search guided selling processes..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0); // Reset to first page on search
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: '250px' }}
          />
        </ArgonBox>

        {/* Processes List */}
        {paginatedProcesses.length > 0 ? (
          <>
            {paginatedProcesses.map((processGroup, index) => {
          // Safety checks for process data
          if (!processGroup || !processGroup.guidedSellingProcess) {
            return null;
          }

          const { guidedSellingProcess, guidedSellingQuestions } = processGroup;
          const isSelected = isProcessSelected(processGroup);
          const processId = guidedSellingProcess?.sfId || guidedSellingProcess?.id;

          return (
            <Card key={index} sx={{ mb: 1 }}>
              <ArgonBox
                p={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                onClick={(e) => {
                  // Only handle row click if not clicking on checkbox
                  if (!e.target.closest('input[type="checkbox"]')) {
                    setExpandedProcess(expandedProcess === processId ? null : processId);
                  }
                }}
                sx={{ cursor: 'pointer' }}
              >
                <ArgonBox display="flex" alignItems="center">
                  <Checkbox
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleProcessSelect(processGroup, e.target.checked);
                    }}
                    size="small"
                    sx={{ zIndex: 10, position: 'relative' }}
                  />
                  <ArgonBox ml={1}>
                    <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                      {guidedSellingProcess?.name || `Process ${index + 1}`}
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="textSecondary">
                      {guidedSellingQuestions?.length || 0} questions •
                      {guidedSellingProcess?.fields?.SBQQ__Default__c === 'true' ? ' Default' : ''} •
                      {guidedSellingProcess?.fields?.SBQQ__GuidedOnly__c === 'true' ? ' Guided Only' : ''}
                    </ArgonTypography>
                  </ArgonBox>
                </ArgonBox>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedProcess(expandedProcess === processId ? null : processId);
                  }}
                >
                  <Icon>{expandedProcess === processId ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                </IconButton>
              </ArgonBox>

              <Collapse in={expandedProcess === processId} timeout="auto" unmountOnExit>
                <ArgonBox sx={{ borderTop: `1px solid ${grey[300]}`, p: 2 }}>
                  {guidedSellingQuestions &&
                    Array.isArray(guidedSellingQuestions) &&
                    guidedSellingQuestions.length > 0 && (
                      <ArgonBox>
                        {guidedSellingQuestions.map((questionGroup, qIndex) => {
                          // Safety checks for question data
                          if (!questionGroup || !questionGroup.guidedSellingQuestion) {
                            return null;
                          }

                          const { guidedSellingQuestion, conditions } = questionGroup;
                          const questionId = guidedSellingQuestion?.sfId || guidedSellingQuestion?.id;
                          const isQuestionExpanded = expandedQuestion === questionId;

                          return (
                            <Card key={qIndex} sx={{ mb: 1 }}>
                              <ArgonBox
                                p={2}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                onClick={() => setExpandedQuestion(isQuestionExpanded ? null : questionId)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <ArgonBox display="flex" alignItems="center">
                                  <ArgonBox ml={1}>
                                    <ArgonTypography variant="h6" fontWeight="medium" color="secondary">
                                      {guidedSellingQuestion?.name || `Question ${qIndex + 1}`}
                                    </ArgonTypography>
                                    <ArgonTypography variant="caption" color="textSecondary">
                                      {conditions?.length || 0} conditions
                                    </ArgonTypography>
                                  </ArgonBox>
                                </ArgonBox>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedQuestion(isQuestionExpanded ? null : questionId);
                                  }}
                                >
                                  <Icon>{isQuestionExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
                                </IconButton>
                              </ArgonBox>

                              <Collapse in={isQuestionExpanded} timeout="auto" unmountOnExit>
                                <ArgonBox sx={{ borderTop: `1px solid ${grey[300]}`, p: 2 }}>
                                  {guidedSellingQuestion?.fields && (
                                    <ArgonBox mb={2} sx={{ pl: 2 }}>
                                      {renderFields(guidedSellingQuestion.fields)}
                                    </ArgonBox>
                                  )}

                                  {conditions && Array.isArray(conditions) && conditions.length > 0 && (
                                    <ArgonBox sx={{ pl: 2 }}>
                                      {conditions.map((condition, cIndex) => {
                                        // Safety checks for condition data
                                        if (!condition || !condition.condition) {
                                          return null;
                                        }

                                        const conditionId = condition.condition?.sfId || condition.condition?.id;
                                        const isConditionExpanded = expandedCondition === conditionId;

                                        return (
                                          <Card key={cIndex} sx={{ mb: 1 }}>
                                            <ArgonBox
                                              p={2}
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="space-between"
                                              onClick={() =>
                                                setExpandedCondition(isConditionExpanded ? null : conditionId)
                                              }
                                              sx={{ cursor: 'pointer' }}
                                            >
                                              <ArgonBox display="flex" alignItems="center">
                                                <ArgonBox ml={1}>
                                                  <ArgonTypography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    color="secondary"
                                                  >
                                                    {condition.condition?.name || `Condition ${cIndex + 1}`}
                                                  </ArgonTypography>
                                                </ArgonBox>
                                              </ArgonBox>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setExpandedCondition(isConditionExpanded ? null : conditionId);
                                                }}
                                              >
                                                <Icon>
                                                  {isConditionExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                                                </Icon>
                                              </IconButton>
                                            </ArgonBox>

                                            <Collapse in={isConditionExpanded} timeout="auto" unmountOnExit>
                                              <ArgonBox sx={{ borderTop: `1px solid ${grey[300]}`, p: 2 }}>
                                                {condition.condition?.fields && (
                                                  <ArgonBox>{renderFields(condition.condition.fields)}</ArgonBox>
                                                )}
                                              </ArgonBox>
                                            </Collapse>
                                          </Card>
                                        );
                                      })}
                                    </ArgonBox>
                                  )}
                                </ArgonBox>
                              </Collapse>
                            </Card>
                          );
                        })}
                      </ArgonBox>
                    )}
                </ArgonBox>
              </Collapse>
            </Card>
          );
        })}

        {/* Pagination */}
        {pageCount > 1 && (
          <ArgonBox display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={pageCount}
              page={page + 1}
              onChange={(e, newPage) => setPage(newPage - 1)}
              color="primary"
            />
          </ArgonBox>
        )}
          </>
        ) : (
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography variant="body2" color="textSecondary">
              {searchTerm ? 'No guided selling processes match your search.' : 'No guided selling processes found.'}
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>
    </Card>
  );
}

export default RevenueBilling;
